import React, { useState } from 'react';

import { LayoutSingleColumn, NamedLink, Page, SellerInquiryModal } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './SellerGrowthPage.module.css';

const platformComparison = [
  { label: ['Inventory', 'Sync'], value: 8, colorClass: 'olive' },
  { label: ['Site', 'Maintenance'], value: 6, colorClass: 'DarkGreen' },
  { label: ['Vendor', 'Management'], value: 12, colorClass: 'gray' },
  { label: ['Order', 'Fulfillment'], value: 9.5, colorClass: 'charcoal' },
];

const chartTicks = [12, 9, 6, 3, 0];

const pillars = [
  {
    title: 'Inventory Management',
    description:
      'Professional dashboard to manage your local stock and digital listings in one place. Real-time updates across all channels mean you\'ll never oversell or disappoint customers',
  },
  {
    title: 'National Distribution',
    description:
      'Tap into expanded catalogs with built-in fulfillment logic. Sell products you don\'t stock on your shelves—we handle the logistics and shipping automatically.',
  },
  {
    title: 'Marketplace & Edge',
    description:
      'Monetize your website traffic instantly with our seamless e-commerce iFrame for just $50/month. Plus, gain exposure on the national Bowtackle Marketplace.',
  },
];

const transformationSteps = [
  {
    number: '1',
    side: 'left',
    title: 'Apply to Sell',
    description:
      'Visit bowtackle.com and verify your pro-shop status. We review applications within 48 hours to ensure quality dealers join our network.',
  },
  {
    number: '2',
    side: 'right',
    title: 'Activate Your Interface',
    description:
      'Set up your complimentary Bowtackle Inventory Management System. Our team provides personalized training to get you comfortable with the dashboard.',
  },
  {
    number: '3',
    side: 'left',
    title: 'Connect Stripe',
    description:
      'Secure, private payouts handled by the industry leader in fintech. Your financial data stays protected with bank-level encryption.',
  },
  {
    number: '4',
    side: 'right',
    title: 'Deploy BowTackle Edge',
    description:
      'Embed the storefront into your own domain. Replace your old website with a high-performance engine that converts visitors into buyers.',
  },
];

const reachCards = [
  {
    image: '/static/images/card_1.png',
    title: 'Social Media Integration',
    description:
      'Push direct product links to Facebook and Instagram with one click. Turn your social presence into a sales channel without leaving the Bowtackle dashboard.',
  },
  {
    image: '/static/images/card_2.png',
    title: 'Monetize Your Traffic',
    description:
      'Use the Bowtackle Edge iFrame to keep customers on your site while they shop live inventory. Seamless checkout experience builds trust and boosts conversions.',
  },
  {
    image: '/static/images/card_3.png',
    title: 'Drive In-Person Visits',
    description:
      'Use digital deals to bring buyers through your physical front door. A-1 Archery saw a 26% increase in engagement using this exact strategy.',
  },
];

const metrics = [
  {
    value: '26%',
    label: 'Increase in Engagement',
    description: 'A-1 Archery boosted customer interaction using Bowtackle\'s integrated digital marketing tools',
  },
  {
    value: '36',
    label: 'Hours Saved Weekly',
    description: 'Average time savings on inventory management and order fulfillment tasks.',
  },
  {
    value: '$50',
    label: 'Cost Per Month',
    description: 'Flat-rate pricing for professional storefront embedded on your domain with Bowtackle Edge.',
  },
  {
    value: '10%',
    label: 'Conversion Rate',
    description: 'Simple, transparent fee structure with no hidden costs or surprise charges.',
  },
];

const faqs = [
  {
    question: 'Is my data safe?',
    answer:
      'Absolutely. Stripe manages all financial details privately with bank-level encryption. Bowtackle never sees your banking information or customer payment data.',
  },
  {
    question: 'How long does setup take?',
    answer:
      'Most shops are live within 5-7 business days. Our team provides hands-on support throughout onboarding to ensure a smooth transition from your current system.',
  },
  {
    question: 'What are the fees?',
    answer:
      'No monthly fees for basic marketplace access. We charge a straightforward 10% commission on completed sales. The Edge iFrame is a flat $50 per month with no additional costs.',
  },
  {
    question: 'Can I keep my existing website?',
    answer:
      'Yes! Bowtackle Edge integrates seamlessly with your current site via iFrame, or you can migrate entirely to our platform—your choice based on your business needs.',
  },
];

const SellerGrowthPage = () => {
  const title = 'Sell More. Better Margins. Less Labor. | BowTackle';
  const description =
    'A marketing page for pro shops exploring BowTackle, BowTackle Edge, and a more efficient way to sell online.';
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);

  return (
    <Page className={css.root} title={title} description={description} scrollingDisabled={false}>
      <LayoutSingleColumn
        className={css.page}
        mainColumnClassName={css.mainColumn}
        topbar={<TopbarContainer currentPage="BecomeASellerPage" />}
        footer={<FooterContainer />}
      >
        <div className={css.content}>
          <section className={css.heroSection}>
            <div className={css.heroText}>
              <h1 className={css.heroTitle}>Sell More. Better Margins. Less Labor.</h1>
              <p className={css.heroDescription}>
                The Bowtackle Solution is the only all-in-one ecosystem designed specifically for the archery industry. We've integrated inventory management, national distribution, and high-traffic e-commerce into a single platform—so you can focus on serving customers, not managing software.
              </p>
              <div className={css.heroActions}>
                <NamedLink
                  name="SignupForUserTypePage"
                  params={{ userType: 'provider' }}
                  className={css.primaryButton}
                >
                  Apply to Sell
                </NamedLink>
                <button
                  type="button"
                  className={css.secondaryButton}
                  onClick={() => setIsInquiryModalOpen(true)}
                >
                  Request Information
                </button>
              </div>
            </div>
            <div className={css.heroMedia}>
              <img src="/static/images/laptop_1.png" alt="BowTackle seller dashboard on a laptop" />
            </div>
          </section>

          <section className={css.problemSection}>
            <div className={css.problemImageWrap}>
              <img src="/static/images/arbol_2.png" alt="Hunter in a tree stand" />
            </div>
            <div className={css.problemTextWrap}>
              <h2 className={css.sectionTitle}>Stop Paying the Shopify Tax</h2>
              <div className={css.problemContentSplit}>
                <div className={css.problemCopy}>
                  <h3 className={css.splitTitle}>The Problem with Generic Platforms</h3>
                  <p className={css.sectionCopy}>
                    Traditional e-commerce solutions drain your profits with expensive plugins, complicated vendor integrations, and constant maintenance headaches. You're an archery expert—not a web developer.
                  </p>
                </div>
                <div className={css.solutionPanel}>
                  <h3 className={css.solutionTitle}>The BowTackle Solution</h3>
                  <ul className={css.solutionList}>
                    <li>Automated updates: We handle the technical complexity</li>
                    <li>Direct catalog access: National inventory without the overhead</li>
                    <li>Industry-specific: Built by archery experts for pro shops</li>
                    <li>Zero plugins needed: Everything's built-in from day one</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className={css.comparisonSection}>
            <div className={css.comparisonLayout}>
              <div className={css.comparisonCopy}>
                <h2 className={css.comparisonTitle}>
                  Traditional Platform
                  <br />
                  vs. Bowtackle
                </h2>
                <p className={css.sectionCopy}>
                  With Shopify or generic platforms, you&apos;re constantly managing manual syncing,
                  chasing vendor feeds, updating plugins, and troubleshooting technical issues.
                  Bowtackle eliminates all of that.
                </p>
                <p className={css.sectionCopy}>
                  Pro shops using The Bowtackle Solution save an average of 36 hours per week on
                  digital operations. That&apos;s time you can spend with customers on the range,
                  tuning equipment, or growing your business.
                </p>
              </div>
              <div className={css.chartWrap}>
                <div className={css.chartTitle}>Hours lost without Bowtackle: 36 per week</div>
                <div className={css.chartFrame}>
                    <div className={css.chartYAxis}>
                      {chartTicks.map(tick => (
                        <span key={tick} className={css.chartTickLabel}>
                          {tick}
                        </span>
                      ))}
                    </div>
                    <div className={css.chartPlot}>
                      {chartTicks.map(tick => (
                        <div key={tick} className={css.chartGridLine} />
                      ))}
                      <div className={css.chartBars}>
                        {platformComparison.map(item => (
                          <div key={item.label.join('-')} className={css.chartItem}>
                            <div
                              className={`${css.chartBar} ${css[`chartBar${item.colorClass[0].toUpperCase()}${item.colorClass.slice(1)}`]}`}
                              style={{ height: `${(item.value / 12) * 100}%` }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className={css.chartLabels}>
                    {platformComparison.map(item => (
                      <span key={item.label.join('-')} className={css.chartLabel}>
                        {item.label[0]}
                        <br />
                        {item.label[1]}
                      </span>
                    ))}
                  </div>
              </div>
            </div>
          </section>

          <section id="pillars" className={css.pillarsSection}>
            <div className={css.sectionHeaderCentered}>
              <h2 className={css.sectionTitle}>The Three Pillars of the BowTackle Solution</h2>
              <p className={css.sectionCopy}>
               
              </p>
            </div>
            <div className={css.pillarsGrid}>
              {pillars.map(pillar => (
                <article key={pillar.title} className={css.pillarCard}>
                  <div className={css.pillarText}>
                    <h3 className={css.pillarTitle}>{pillar.title}</h3>
                    <p className={css.pillarDescription}>{pillar.description}</p>
                  </div>
                  <img
                    src="/static/images/pillar.png"
                    alt=""
                    aria-hidden="true"
                    className={css.pillarImage}
                  />
                </article>
              ))}
            </div>
          </section>

          <section className={css.stepsSection}>
            <div className={css.stepsHeader}>
              <h2 className={css.stepsTitle}>Your Path to Digital Transformation</h2>
              <p className={css.stepsIntro}>
                Don&apos;t get left behind with outdated, manual e-commerce tools that drain your
                time and profits. Join the growing network of pro shops winning with The Bowtackle
                Solution, the platform built specifically for your industry&apos;s unique needs.
              </p>
            </div>
            <div className={css.timeline}>
              {transformationSteps.map(step => (
                <article
                  key={step.number}
                  className={`${css.stepRow} ${
                    step.side === 'left' ? css.stepRowLeft : css.stepRowRight
                  }`}
                >
                  <div className={css.stepContentLeft}>
                    {step.side === 'left' ? (
                      <div className={css.stepContent}>
                        <h3 className={css.stepTitle}>{step.title}</h3>
                        <p className={css.stepText}>{step.description}</p>
                      </div>
                    ) : null}
                  </div>
                  <div className={css.stepCenter}>
                    <div className={css.stepLineTop} />
                    <div className={css.stepBadge}>{step.number}</div>
                    <div className={css.stepLineBottom} />
                  </div>
                  <div className={css.stepContentRight}>
                    {step.side === 'right' ? (
                      <div className={css.stepContent}>
                        <h3 className={css.stepTitle}>{step.title}</h3>
                        <p className={css.stepText}>{step.description}</p>
                      </div>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className={css.cardsSection}>
            <div className={css.sectionHeaderLeft}>
              <h2 className={css.sectionTitle}>Reach Your Customers Where They Are</h2>
              <p className={css.sectionCopy}>

              </p>
            </div>
            <div className={css.cardsGrid}>
              {reachCards.map(card => (
                <article key={card.title} className={css.imageCard}>
                  <img src={card.image} alt={card.title} className={css.imageCardMedia} />
                  <div className={css.imageCardBody}>
                    <h3 className={css.cardTitle}>{card.title}</h3>
                    <p className={css.cardText}>{card.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className={css.metricsSection}>
            <div className={css.sectionHeaderLeft}>
              <h2 className={css.sectionTitle}>Real Results from Pro Shops Like Yours</h2>
              <p className={css.sectionCopy}>
              
              </p>
            </div>
            <div className={css.metricsGrid}>
              {metrics.map(metric => (
                <article key={metric.label} className={css.metricCard}>
                  <div className={css.metricValue}>{metric.value}</div>
                  <h3 className={css.metricLabel}>{metric.label}</h3>
                  <p className={css.metricText}>{metric.description}</p>
                </article>
              ))}
            </div>
          </section>

          <section className={css.faqSection}>
            <div className={css.sectionHeaderCentered}>
              <h2 className={css.sectionTitle}>Frequently Asked Questions</h2>
            </div>
            <div className={css.faqGrid}>
              {faqs.map(item => (
                <article key={item.question} className={css.faqCard}>
                  <h3 className={css.faqQuestion}>{item.question}</h3>
                  <p className={css.faqAnswer}>{item.answer}</p>
                </article>
              ))}
            </div>
          </section>

          <section className={css.ctaSection}>
            <img
              src="/static/images/cta.png"
              alt="BowTackle marketplace experience"
              className={css.ctaBackground}
            />
            <div className={css.ctaOverlay} />
            <div className={css.ctaInner}>
              <div className={css.ctaContent}>
                <h2 className={css.ctaTitle}>Ready to Transform Your Business?</h2>
                <p className={css.ctaText}>
                  The archery industry is evolving rapidly, and your competitors are already making the switch to modern, integrated solutions. Position your pro shop for long-term success with technology that works as hard as you do.
                  <br />
                  <br />
                  Apply today to join the Bowtackle network and discover how easy digital retail can be when you have the right tools. Our team is standing by to answer your questions and guide you through every step of the process.
                </p>
                <div className={css.heroActions}>
                  <NamedLink
                    name="SignupForUserTypePage"
                    params={{ userType: 'provider' }}
                    className={css.primaryButton}
                  >
                    Apply to Sell
                  </NamedLink>
                  <a href="mailto:integrations@bowtackle.com" className={css.secondaryButton}>
                    Schedule a Demo
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
        <SellerInquiryModal
          isOpen={isInquiryModalOpen}
          onClose={() => setIsInquiryModalOpen(false)}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

export default SellerGrowthPage;
